import {
  Component,
  Input,
  ViewChild,
  EventEmitter,
  Output,
} from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { IScoreMessageAdherence } from 'src/app/interfaces/iscore-message-adherence';
import { Clipboard } from '@angular/cdk/clipboard';
import { IScoreDetails } from 'src/app/interfaces/iresults-conversational';
import { IScoreOverallConversational } from 'src/app/interfaces/iscore-overall-conversational';
import { PracticeType } from 'src/app/utils/enums/result/resultConversational.enum';
import { TabName } from 'src/app/utils/enums/result/resultConversational.enum';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { LocaleService } from 'src/app/services/locale.service';
import { AmplitudeTrackingEvents } from 'src/app/utils/enums/amplitude/amplitudeTrackingEvents.enum';
import { IFlattenedMessageAdherenceSection } from 'src/app/interfaces/iflattenedMessageAdherenceSection';
import { TranslocoService } from '@jsverse/transloco';
import { TranslationService } from 'src/app/services/translation.service';
import { AppConstants } from 'src/app/app-constants';

@Component({
  selector: 'app-new-message-adherence',
  templateUrl: './new-message-adherence.component.html',
  styleUrls: ['./new-message-adherence.component.scss'],
  standalone: false
})
export class NewMessageAdherenceComponent {
  @ViewChild(MatMenuTrigger) tooltipMenu!: MatMenuTrigger;

  @Input() practiceCallUuid: string = '';
  @Input() weightAverage: IScoreDetails | undefined = undefined;
  @Input() scoreCriteria: number = 0;
  @Input() messageAdherences: IScoreMessageAdherence[] = [];
  @Input() messageAdherencesV2: IFlattenedMessageAdherenceSection[] = [];
  @Input() overallScoreWithOneSection: string | undefined = undefined;
  @Input() scoreOverall: IScoreOverallConversational | undefined = undefined;
  @Input() practiceType: string | undefined = 'PRACTICE';
  @Input() remainingAttempts: number | undefined = 0;
  @Input() isMaxAttemptsReached: boolean = false;
  @Input() scoredAt: string = '';
  @Input() callType: string | undefined = 'CONVERSATIONAL';
  @Input() showMissedFacts: boolean | undefined = false;
  @Input() isMessageAdherenceV2: boolean = false;
  @Input() weightageCalculation: boolean = false;
  @Input() assignmentStatus: string | undefined;
  @Input() hideCallButton: boolean = false;
  @Output() callBtnClicked = new EventEmitter<void>();
  translationLoaded: boolean = false;
  AppConstants = AppConstants;

  constructor(
    private clipboard: Clipboard,
    private amplitudeService: AmplitudeService,
    private translocoService: TranslocoService,
    private translationService: TranslationService,
    protected localeService: LocaleService,
  ) { }

  ngOnInit() {
    this.translationService.loadScopedTranslation('results').then(() => {
      this.translationLoaded = true;
    })
    this.amplitudeService.trackEvent(
      AmplitudeTrackingEvents.MESSAGE_ADHERENCE_PAGE_VIEW,
      {
        call_type: this.callType,
        assignment_type: this.practiceType,
      }
    );
    console.log('weightageCalculation received:', this.weightageCalculation);
  }

  copyText() {
    this.clipboard.copy(this.practiceCallUuid);
    this.tooltipMenu.openMenu();

    setTimeout(() => {
      this.tooltipMenu.closeMenu();
    }, 3000);
  }

  practiceAgain(event: Event) {
    this.amplitudeService.trackEvent(
      AmplitudeTrackingEvents.MESSAGE_ADHERENCE_CLICK_PRACTICE_AGAIN,
      {
        call_type: this.callType,
        assignment_type: this.practiceType,
        tab_name: TabName.MessageAdherence,
      }
    );
    this.callBtnClicked.emit();
  }

  getPointsLabel(totalNum: number): string {
    return this.translocoService.translate(
      totalNum === 1 ? 'POINT_LABEL' : 'POINTS_LABEL',
      {},
      'results'
    );
  }

  getButtonText() {
    if (this.practiceType === PracticeType.CERTIFICATION) return `${this.translocoService.translate('TRY_AGAIN', {}, 'results')}`;
    else return `${this.translocoService.translate('PRACTICE_AGAIN', {}, 'results')}`;
  }

  dynamicText(): string {
    const highlightedTalkingPointsCount = this.messageAdherences.filter(
      (item) => item.score === 2 || item.score === 3
    ).length;

    return this.getDynamicMessage(highlightedTalkingPointsCount, this.messageAdherences.length);
  }

  dynamicTextMessageAdherenceV2(): string {
    const highlightedTalkingPointsCount = this.messageAdherencesV2.reduce(
      (acc, item) => acc + item.numIncludedCorrectly,
      0
    );
    const totalTalkingPointsCount = this.messageAdherencesV2.reduce(
      (acc, item) => acc + item.totalNum,
      0
    );
    return this.getDynamicMessage(highlightedTalkingPointsCount, totalTalkingPointsCount);
  }
  //helper function to get the dynamic message for both message adherence and message adherence v2
  // Full-sentence keys so each language can place the counts where its grammar needs them
  getDynamicMessage(covered: number, total: number): string {
    const params = { covered, total };
    if (this.scoreCriteria === 0) {
      return this.translocoService.translate('COVERED_TALKING_POINTS_SUMMARY', params, 'results');
    }
    
    if (this.weightAverage?.average !== undefined && this.weightAverage.average >= this.scoreCriteria) {
      return this.translocoService.translate('COVERED_TALKING_POINTS_POSITIVE', params, 'results');
    } else {
      return this.translocoService.translate('COVERED_TALKING_POINTS_MIXED', params, 'results');
    }
  }
}

